import type { Ring, Vec2, Vec3 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { orientedFace, planPoint } from '../../solids';
import { panelWall } from '../../panels';
import { edgePanel } from '../masonry';

/**
 * A timber dormer clad in lead with an ogee head ("ezelsrugboog"), such as housed the Oude Kerk's
 * clock dials at the foot of its spire (Janse 2004, p. 135): its front stands on a counter-clockwise
 * plan edge from `from` to `to` and it runs `depth` straight back, into the spire it leans on.
 */
export interface OgeeDormerSpec {
  readonly from: Vec2;
  readonly to: Vec2;
  readonly y0: number;
  readonly springY: number;
  readonly apexY: number;
  readonly depth: number;
}

/* Points on each half of the head between its springing and its apex. */
const HEAD_STEPS = 4;

/** The ogee's rise at t from the springing (0) to the apex (1): convex below its middle, concave above. */
function ogee(t: number): number {
  return t <= 0.5 ? 0.5 * Math.sin(Math.PI * t) : 1 - 0.5 * Math.sin(Math.PI * (1 - t));
}

/** The front's outline in its own panel: `width` along it, the springing and the apex measured up from its foot. */
export function ogeeOutline(width: number, spring: number, apex: number): Ring {
  const half = width / 2;
  const at = (t: number, side: 1 | -1): Vec2 => [half + side * half * (1 - t), spring + (apex - spring) * ogee(t)];
  const right = Array.from({ length: HEAD_STEPS - 1 }, (_, k) => at((k + 1) / HEAD_STEPS, 1));
  const left = Array.from({ length: HEAD_STEPS - 1 }, (_, k) => at((HEAD_STEPS - 1 - k) / HEAD_STEPS, -1));
  return [[0, 0], [width, 0], [width, spring], ...right, [half, apex], ...left, [0, spring]];
}

export function ogeeDormer(builder: MeshBuilder, style: VertexStyle, frame: Frame, d: OgeeDormerSpec): void {
  const du = d.to[0] - d.from[0];
  const dv = d.to[1] - d.from[1];
  const width = Math.hypot(du, dv);
  const along: Vec2 = [du / width, dv / width];
  const inward: Vec2 = [-along[1], along[0]];
  const back = (p: Vec2): Vec2 => [p[0] + inward[0] * d.depth, p[1] + inward[1] * d.depth];
  const outline = ogeeOutline(width, d.springY - d.y0, d.apexY - d.y0);
  const styles = { wall: style, opening: style };
  panelWall(builder, styles, frame, edgePanel(d.from, d.to, d.y0), outline, []);
  const mirrored: Ring = outline.map((p): Vec2 => [width - p[0], p[1]]);
  panelWall(builder, styles, frame, edgePanel(back(d.to), back(d.from), d.y0), mirrored, []);
  const point = (p: Vec2, depth: number): Vec3 =>
    planPoint(d.from[0] + along[0] * p[0] + inward[0] * depth, d.y0 + p[1], d.from[1] + along[1] * p[0] + inward[1] * depth);
  outline.forEach((p, i) => {
    const q = outline[(i + 1) % outline.length]!;
    if (i === 0) return;
    const ea = q[0] - p[0];
    const eb = q[1] - p[1];
    const outward: Vec3 = [along[0] * eb, -ea, -along[1] * eb];
    orientedFace(builder, style, frame, [point(p, 0), point(q, 0), point(q, d.depth), point(p, d.depth)], outward);
  });
}
